/* eslint-disable camelcase */
export interface NavItemRange {
  start: moment.Moment;
  end: moment.Moment;
}

export interface NavItem {
  display: string;
  range: NavItemRange;
  id: string;
}

export interface NavItems {
  [key: string]: NavItem;
}

export interface Tag {
  name: string;
  count: number;
}

export interface ComputedTag {
  name: string;
  percentage: number;
}

export interface TopTags {
  total_conversations: number;
  tags: Tag[];
}

export interface TopicSentiments {
  positive: number;
  negative: number;
  neutral: number;
}

export interface Topic {
  label: string;
  total: number;
  sentiments: TopicSentiments;
}

export interface Category {
  name: string;
  topics: Topic[];
}

export interface TopTopics {
  total_conversations: number;
  categories: Category[];
}

export interface SubscriberMetrics {
  total_subscribers: number;
  matching_subscribers: number;
}

export interface ConversationMetrics {
  total_conversations: number;
  matching_conversations: number;
  active_conversations: number;
}

export type ConversationIntelligenceMetricsData = SubscriberMetrics &
  ConversationMetrics;

export enum ConversationIntelligenceStatus {
  NO_CONVERSATIONS = 'NO_CONVERSATIONS',
  PARTIAL_INSIGHTS = 'PARTIAL_INSIGHTS',
  COMPLETE_INSIGHTS = 'COMPLETE_INSIGHTS',
}

// Shopper Insights Search and Filter Controls
export interface ShopperInsightsControls {
  page: number;
  shopIds: number[];
  agentIds: string[] | number[];
  searchText: string;
  searchDirection: string;
  filters: string[];
  topicLabel?: string;
  tag?: string;
  onlyShopper?: boolean;
  sortBy: string;
  dateRangeStart?: string;
  dateRangeEnd?: string;
  maxPageSize?: number;
}

export const FILTER_KEYS = {
  LIVE: 'LIVE',
  INACTIVE: 'INACTIVE',
  HAS_SUMMARY: 'HAS_SUMMARY',
  NO_SUMMARY: 'NO_SUMMARY',
  CONVERTED: 'CONVERTED',
  NOT_CONVERTED: 'NOT_CONVERTED',
  UNSUBSCRIBED: 'UNSUBSCRIBED',
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
  NEUTRAL: 'NEUTRAL',
};

export const STATUS_OPTIONS = {
  [FILTER_KEYS.LIVE]: {
    label: 'Happening now',
    value: FILTER_KEYS.LIVE,
  },
  [FILTER_KEYS.INACTIVE]: {
    label: 'Inactive',
    value: FILTER_KEYS.INACTIVE,
  },
};

export const SUMMARY_OPTIONS = {
  [FILTER_KEYS.HAS_SUMMARY]: {
    label: 'Has Summary',
    value: FILTER_KEYS.HAS_SUMMARY,
  },
  [FILTER_KEYS.NO_SUMMARY]: {
    label: 'No Summary',
    value: FILTER_KEYS.NO_SUMMARY,
  },
};

export const OUTCOMES_OPTIONS = {
  [FILTER_KEYS.CONVERTED]: {
    label: 'Converted',
    value: FILTER_KEYS.CONVERTED,
  },
  [FILTER_KEYS.NOT_CONVERTED]: {
    label: 'Did not convert',
    value: FILTER_KEYS.NOT_CONVERTED,
  },
  [FILTER_KEYS.UNSUBSCRIBED]: {
    label: 'Unsubscribed',
    value: FILTER_KEYS.UNSUBSCRIBED,
  },
};

export const SENTIMENT_OPTIONS = {
  [FILTER_KEYS.POSITIVE]: {
    label: 'Positive',
    value: FILTER_KEYS.POSITIVE,
  },
  [FILTER_KEYS.NEUTRAL]: {
    label: 'Neutral',
    value: FILTER_KEYS.NEUTRAL,
  },
  [FILTER_KEYS.NEGATIVE]: {
    label: 'Negative',
    value: FILTER_KEYS.NEGATIVE,
  },
};

export const MESSAGE_DIRECTION_KEYS = {
  INBOUND: 'INBOUND',
  OUTBOUND: 'OUTBOUND',
} as const;

export type MessageDirectionKeys =
  typeof MESSAGE_DIRECTION_KEYS[keyof typeof MESSAGE_DIRECTION_KEYS];

export const SEARCH_DIRECTION_KEYS = {
  ALL: 'ALL',
  INBOUND: MESSAGE_DIRECTION_KEYS.INBOUND,
  OUTBOUND: MESSAGE_DIRECTION_KEYS.OUTBOUND,
};

export const SEARCH_DIRECTION = {
  [SEARCH_DIRECTION_KEYS.ALL]: {
    label: 'Search all messages',
    value: SEARCH_DIRECTION_KEYS.ALL,
  },
  [SEARCH_DIRECTION_KEYS.INBOUND]: {
    label: 'Search inbound only',
    value: SEARCH_DIRECTION_KEYS.INBOUND,
  },
  [SEARCH_DIRECTION_KEYS.OUTBOUND]: {
    label: 'Search outbound only',
    value: SEARCH_DIRECTION_KEYS.OUTBOUND,
  },
};

export const SORT_BY_KEYS = {
  CREATED_AT_ASC: 'CREATED_AT_1',
  CREATED_AT_DESC: 'CREATED_AT_-1',
  MOST_RECENT_INBOUND: 'MOST_RECENT_INBOUND',
  MOST_RECENT_OUTBOUND: 'MOST_RECENT_OUTBOUND',
};

export const SORT_BY = {
  [SORT_BY_KEYS.CREATED_AT_ASC]: {
    label: 'Conversations (oldest First)',
    value: SORT_BY_KEYS.CREATED_AT_ASC,
  },
  [SORT_BY_KEYS.CREATED_AT_DESC]: {
    label: 'Conversations (newest First)',
    value: SORT_BY_KEYS.CREATED_AT_DESC,
  },
  [SORT_BY_KEYS.MOST_RECENT_INBOUND]: {
    label: 'Inbound message (newest first)',
    value: SORT_BY_KEYS.MOST_RECENT_INBOUND,
  },
  [SORT_BY_KEYS.MOST_RECENT_OUTBOUND]: {
    label: 'Outbound message (newest first)',
    value: SORT_BY_KEYS.MOST_RECENT_OUTBOUND,
  },
};

export const FILTER_LABEL_LOOKUP = {
  ...STATUS_OPTIONS,
  ...SUMMARY_OPTIONS,
  ...OUTCOMES_OPTIONS,
  ...SENTIMENT_OPTIONS,
};
